import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { analyticsApi, type AnalyticsEventType } from '@/api/analytics'

const SESSION_KEY = 'edive-analytics-session'

// Analytics are always active — users consent via ToS at registration.
// All data uses a pseudonymous anonymousId (localStorage UUID); no third-party cookies.

export function useAnalytics() {
  const location = useLocation()
  const sessionIdRef = useRef<string | null>(sessionStorage.getItem(SESSION_KEY))
  const initializingRef = useRef(false)

  // Initialize session once on mount
  useEffect(() => {
    if (sessionIdRef.current || initializingRef.current) return

    initializingRef.current = true
    analyticsApi
      .createSession()
      .then((res) => {
        sessionIdRef.current = res.sessionId
        sessionStorage.setItem(SESSION_KEY, res.sessionId)
      })
      .catch(() => {})
      .finally(() => { initializingRef.current = false })
  }, [])

  // Track pageview on each route change
  useEffect(() => {
    const sessionId = sessionIdRef.current
    if (!sessionId) return

    analyticsApi
      .trackEvent({
        sessionId,
        eventType: 'PAGEVIEW',
        eventName: location.pathname.slice(0, 100),
        pagePath: location.pathname,
      })
      .catch(() => {})
  }, [location.pathname, location.search])

  const trackEvent = useCallback(
    (eventName: string, payload?: Record<string, unknown>, eventType?: AnalyticsEventType) => {
      const sessionId = sessionIdRef.current
      if (!sessionId) return

      analyticsApi
        .trackEvent({
          sessionId,
          eventType: eventType ?? 'FEATURE_CLICK',
          eventName,
          pagePath: location.pathname,
          payload,
        })
        .catch(() => {})
    },
    [location.pathname]
  )

  return { trackEvent }
}
